import {memo, useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {
  imageUrlAdapterAspectRatios,
  imageUrlAdapterImageTypes,
  imageUrlAdapterTransformTypes,
  useImageUrlAdapter
} from '@adv-mt/mt-hook-use-image-url-adapter'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'

import GalleryBasicBadges from './components/Badges.js'
import GalleryBasicFullSizeActionButton from './components/FullSizeActionButton.js'
import GalleryBasicSlider from './components/Slider/index.js'
import GalleryBasicTags from './components/Tags.js'
import ThumbnailsWrapper from './components/ThumbnailCarousel/index.js'
import GalleryBasicVideoButton from './components/VideoButton.js'
import {
  BASE_CLASS,
  COUNTER_POSITIONS,
  FULL_SIZE_BUTTON_POSITIONS,
  GALLERY_ASPECT_RATIO,
  NO_OP,
  SLIDER_IMAGE_BASE_CLASS,
  SLIDER_IMAGE_DESIGNS,
  SLIDER_IMAGE_SINGLE_CLASS,
  SLIDER_IMAGE_SIZE_CONFIG,
  SLIDER_IMAGE_SIZES,
  SLIDER_OPTIONS,
  SLIDER_WRAPPER_CLASS,
  TEST_ID_GALLERY_BASIC_WRAPPER,
  TEST_ID_MAIN_SLIDER
} from './config.js'
import {blockScrollPage, enableScrollPage} from './utils.js'

const GalleryBasic = ({
  aspectRatio = GALLERY_ASPECT_RATIO.fullScreen,
  badges = [],
  counterPosition = COUNTER_POSITIONS.bottomCenter,
  displayVideoControls = false,
  enableVideoTag = true,
  enableVideoTagInFullSize = false,
  enableSlideByClick = true,
  enableCounter = false,
  enableCounterInFullSize = false,
  hasLazyLoadSlider = true,
  hideCounterWhenPlayingVideo,
  id,
  images = [],
  isGridLayout = false,
  onVideoClick,
  onVideoButtonClick = NO_OP,
  onVideoPlay,
  selectedImage = 0,
  showFullSizeBtn = false,
  fullSizeButtonPosition = FULL_SIZE_BUTTON_POSITIONS.topRight,
  fullSizeExpandButtonClass,
  showLargeVideoButton = false,
  showThumbnails = true,
  sliderImageDesign = SLIDER_IMAGE_DESIGNS.rounded,
  sliderImageSize = SLIDER_IMAGE_SIZES.SMALL,
  tags = [],
  videoApiRef,
  itemsToPreload,
  disableSliderInMiniature = false
}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const [selectedSlide, setSelectedSlide] = useState(selectedImage)
  const [isFullSize, setIsFullSize] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const isWideAspectRatio = aspectRatio === GALLERY_ASPECT_RATIO.wideScreen

  useMount(() => setIsMounted(true))

  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }
  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)

  const imageSize = SLIDER_IMAGE_SIZE_CONFIG[sliderImageSize]
  const imageWidth = isGridLayout || !isHigherThanMobileResolution ? imageSize.mobile : imageSize.desktop
  const getImagesUrlsAdapted = useImageUrlAdapter()
  const {images: resizedImages} = getImagesUrlsAdapted({
    images,
    options: {
      aspectRatio: isWideAspectRatio
        ? imageUrlAdapterAspectRatios.WIDE_SCREEN
        : imageUrlAdapterAspectRatios.FULL_SCREEN,
      imageWidth,
      imageType: imageUrlAdapterImageTypes.GALLERY,
      transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
    }
  })

  const hasTags = Boolean(tags.length)
  const hasBadges = Boolean(badges.length)
  const hasImages = Boolean(images.length)
  const hasSingleImage = images.length === 1
  const [firstImage = {}] = resizedImages
  const showTags = hasTags && !hasBadges && !isFullSize
  const showBadges = hasBadges && !isFullSize

  const videoPosition = images.findIndex(element => element.isVideo)

  useEffect(() => {
    isFullSize ? blockScrollPage() : enableScrollPage()
    return () => enableScrollPage()
  }, [isFullSize])

  const baseClass = cx(BASE_CLASS, {
    'is-fullSize': isFullSize,
    'has-singleImage': hasSingleImage,
    [`${BASE_CLASS}--wide`]: isWideAspectRatio
  })

  const sliderWrapperClass = cx(SLIDER_WRAPPER_CLASS, {
    [`${SLIDER_WRAPPER_CLASS}--spaced`]: showThumbnails
  })

  const handleClickSlider = e => {
    if (images[selectedSlide].isVideo) return

    const sliderMiddleX = e.currentTarget.offsetWidth / 2
    const sliderClickX = e.nativeEvent.offsetX
    const isClickOnRightSide = sliderClickX > sliderMiddleX
    const nextSlide = isClickOnRightSide ? selectedSlide + 1 : selectedSlide - 1
    if (nextSlide >= 0 && nextSlide < images.length) setSelectedSlide(nextSlide)
  }

  const handleClickFullSizeActionBtn = () => setIsFullSize(!isFullSize)

  const handleAfterSlide = slide => setSelectedSlide(slide)

  const handleSelectThumbnail = ({selectedSlide}) => setSelectedSlide(selectedSlide)

  const openVideoInFullScreen = ({selectedSlide}) => {
    onVideoButtonClick()
    handleSelectThumbnail({selectedSlide})
    showFullSizeBtn && handleClickFullSizeActionBtn()
  }

  const videoIsNotSelected = selectedSlide !== videoPosition

  const galleryBasicSliderProps = {
    counterPosition,
    currentSlide: selectedSlide,
    displayVideoControls,
    enableCounter: enableCounter || (enableCounterInFullSize && isFullSize),
    handleClick: enableSlideByClick ? handleClickSlider : NO_OP,
    hideCounterWhenPlayingVideo,
    images: resizedImages,
    aspectRatio,
    onVideoClick,
    onVideoPlay,
    sliderImageDesign,
    sliderOptions: {
      ...SLIDER_OPTIONS,
      defaultSlide: selectedSlide,
      itemsToPreload,
      onSlideAfter: handleAfterSlide,
      slide: selectedSlide
    },
    videoApiRef
  }

  const firstImageProps = {
    src: firstImage.src,
    className: `${SLIDER_IMAGE_BASE_CLASS} ${SLIDER_IMAGE_SINGLE_CLASS}`,
    alt: firstImage.alt,
    title: firstImage.title,
    fetchPriority: 'high'
  }

  return hasImages ? (
    <div className={baseClass} data-testid={TEST_ID_GALLERY_BASIC_WRAPPER}>
      {isMounted ? (
        <div className={sliderWrapperClass} data-testid={TEST_ID_MAIN_SLIDER}>
          {showTags && <GalleryBasicTags tags={tags} />}
          {showBadges && <GalleryBasicBadges badges={badges} />}
          {showFullSizeBtn && (videoIsNotSelected || isFullSize || disableSliderInMiniature) && (
            <GalleryBasicFullSizeActionButton
              isFullSize={isFullSize}
              onClickFullSizeActionBtn={handleClickFullSizeActionBtn}
              fullSizeButtonPosition={fullSizeButtonPosition}
              fullSizeExpandButtonClass={fullSizeExpandButtonClass}
            />
          )}
          {disableSliderInMiniature && !isFullSize ? (
            <img {...firstImageProps} />
          ) : (
            <GalleryBasicSlider {...galleryBasicSliderProps} />
          )}
          {(enableVideoTag || (isFullSize && enableVideoTagInFullSize)) && videoPosition !== -1 ? (
            <GalleryBasicVideoButton
              handleClick={openVideoInFullScreen}
              id={id}
              selectedSlide={selectedSlide}
              showLargeVideoButton={showLargeVideoButton}
              videoPosition={videoPosition}
            />
          ) : null}
        </div>
      ) : (
        <img {...firstImageProps} />
      )}
      {showThumbnails && !hasSingleImage && (
        <ThumbnailsWrapper
          hasLazyLoadSlider={hasLazyLoadSlider}
          images={images}
          onSelectThumbnail={handleSelectThumbnail}
          selectedSlide={selectedSlide}
        />
      )}
    </div>
  ) : (
    <></>
  )
}

GalleryBasic.displayName = 'GalleryBasic'
GalleryBasic.propTypes = {
  aspectRatio: PropTypes.string,
  badges: PropTypes.array,
  counterPosition: PropTypes.oneOf(Object.values(COUNTER_POSITIONS)),
  displayVideoControls: PropTypes.bool,
  /** Props to control video button visibility */
  enableVideoTag: PropTypes.bool,
  enableVideoTagInFullSize: PropTypes.bool,
  /** Props to control counter visibility */
  enableCounter: PropTypes.bool,
  enableCounterInFullSize: PropTypes.bool,
  enableSlideByClick: PropTypes.bool,
  id: PropTypes.string,
  /** An array of image objects ({src, alt, title}) that will be used for the main slider and the thumbnails  */
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
      title: PropTypes.string
    })
  ).isRequired,
  isGridLayout: PropTypes.bool,
  itemsToPreload: PropTypes.number,
  /** A boolean to determine if the sliders will be lazy loaded using Intersection Observer, default set to true */
  hasLazyLoadSlider: PropTypes.bool,
  hideCounterWhenPlayingVideo: PropTypes.bool,
  onVideoButtonClick: PropTypes.func,
  onVideoClick: PropTypes.func,
  onVideoPlay: PropTypes.func,
  /** A number to tell the component which image to show when loaded, default set to 0 */
  selectedImage: PropTypes.number,
  /** A boolean to show or not the full size selector button, default set to false */
  showFullSizeBtn: PropTypes.bool,
  fullSizeButtonPosition: PropTypes.oneOf(Object.values(FULL_SIZE_BUTTON_POSITIONS)),
  fullSizeExpandButtonClass: PropTypes.string,
  /** A boolean to disable the swipe on the images slider when it is not full screen */
  disableSliderInMiniature: PropTypes.bool,
  /** A boolean to show or not the large video button, default set to false */
  showLargeVideoButton: PropTypes.bool,
  /** A boolean to show or not the thumbnails below the main image, default set to true */
  showThumbnails: PropTypes.bool,
  sliderImageDesign: PropTypes.oneOf(Object.values(SLIDER_IMAGE_DESIGNS)),
  sliderImageSize: PropTypes.oneOf(Object.values(SLIDER_IMAGE_SIZES)),
  tags: PropTypes.array,
  videoApiRef: PropTypes.object
}

const MemoGalleryBasic = memo(GalleryBasic)
MemoGalleryBasic.displayName = 'GalleryBasic'

export {
  COUNTER_POSITIONS,
  FULL_SIZE_BUTTON_POSITIONS,
  SLIDER_IMAGE_DESIGNS,
  SLIDER_IMAGE_SIZES,
  GALLERY_ASPECT_RATIO,
  GalleryBasicFullSizeActionButton
}

export default MemoGalleryBasic
